import React, { useMemo, useState } from "react";
import { MonthKey } from "./types";
import { useFinance } from "./useFinance";
import { budgetProgress, inMonth, monthlyTrend, spendingByCategory, totals } from "./selectors";
import { formatMonth, shiftMonth, toDateKey } from "./format";
import { SummaryTiles } from "./components/SummaryTiles";
import { MonthlyTrend } from "./components/MonthlyTrend";
import { CategoryBreakdown } from "./components/CategoryBreakdown";
import { BudgetList } from "./components/BudgetList";
import { TransactionForm } from "./components/TransactionForm";
import { TransactionList } from "./components/TransactionList";
import "./FinanceTracker.css";

export function FinanceTracker() {
    const { data, categoriesById, addTransaction, deleteTransaction, setBudget, reset } = useFinance();
    const thisMonth = toDateKey(new Date()).slice(0, 7);
    const [month, setMonth] = useState<MonthKey>(thisMonth);

    const monthTransactions = useMemo(() => inMonth(data.transactions, month), [data.transactions, month]);
    const current = useMemo(() => totals(monthTransactions), [monthTransactions]);
    const previous = useMemo(() => totals(inMonth(data.transactions, shiftMonth(month, -1))), [data.transactions, month]);
    // The trend always ends at the later of the selected month and today, so the selection stays visible
    const trendEnd = month > thisMonth ? month : thisMonth;
    const trend = useMemo(() => monthlyTrend(data.transactions, trendEnd, 6), [data.transactions, trendEnd]);
    const byCategory = useMemo(() => spendingByCategory(monthTransactions, data.categories), [monthTransactions, data.categories]);
    const budgets = useMemo(() => budgetProgress(monthTransactions, data.budgets, data.categories), [monthTransactions, data.budgets, data.categories]);

    const exportCsv = () => {
        const escape = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
        const rows = data.transactions
            .slice()
            .sort((a, b) => (a.date < b.date ? -1 : 1))
            .map(t => [t.date, t.type, categoriesById[t.categoryId]?.name || t.categoryId, (t.amount / 100).toFixed(2), t.note].map(escape).join(","));
        const csv = ["date,type,category,amount,note"].concat(rows).join("\n");
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = "transactions.csv";
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="fin">
            <div className="fin-toolbar">
                <div className="fin-month">
                    <button onClick={() => setMonth(shiftMonth(month, -1))} aria-label="Previous month">‹</button>
                    <h2>{formatMonth(month)}</h2>
                    <button onClick={() => setMonth(shiftMonth(month, 1))} aria-label="Next month">›</button>
                    {month !== thisMonth && (
                        <button className="fin-link" onClick={() => setMonth(thisMonth)}>Today</button>
                    )}
                </div>
                <div className="fin-actions">
                    <button className="fin-link" onClick={exportCsv}>Export CSV</button>
                    <button
                        className="fin-link"
                        onClick={() => {
                            if (window.confirm("Reset the demo to its sample data? Your changes will be lost.")) {
                                reset();
                                setMonth(thisMonth);
                            }
                        }}
                    >
                        Reset demo
                    </button>
                </div>
            </div>

            <SummaryTiles current={current} previous={previous} currency={data.currency} />

            <div className="fin-grid">
                <div className="fin-col">
                    <MonthlyTrend data={trend} selected={month} currency={data.currency} onSelect={setMonth} />
                    <CategoryBreakdown items={byCategory} currency={data.currency} />
                </div>
                <div className="fin-col">
                    <TransactionForm categories={data.categories} onAdd={addTransaction} />
                    <BudgetList items={budgets} categories={data.categories} currency={data.currency} onSetBudget={setBudget} />
                </div>
            </div>

            <TransactionList
                transactions={monthTransactions}
                categoriesById={categoriesById}
                currency={data.currency}
                onDelete={deleteTransaction}
            />

            <p className="fin-note">Demo with sample data, saved only in your browser.</p>
        </div>
    );
}
